import React from 'react';
import { Paper, Flex, Box} from '@mantine/core';
import { useFindHashtags } from './NoteTitlePreprocessing';
import { MarkdownViewer } from '../../note/components/MarkdownViewer';

/**
 * 메시지 인터페이스 - 기본 메시지 속성 정의
 */
export interface MessageContent {
    content: string;
    timestamp?: Date;
}

/**
 * 해시태그 스타일 적용 함수 - NoteTitlePreprocessing.tsx의 로직과 동일하게 처리
 */
const useMessageHashtagFormatting = () => {
    // 해시태그 찾는 훅 사용
    const findHashtags = useFindHashtags();
    
    // 메시지 콘텐츠에 해시태그 스타일 적용
    const formatMessageWithHashtags = (text: string): string => {
        if (!text.includes('#')) return text;
        
        // 해시태그 매치 찾기
        const hashtagMatches = findHashtags(text);
        
        // 해시태그가 없으면 원본 텍스트 반환
        if (hashtagMatches.length === 0) return text;
        
        // 텍스트를 순회하며 해시태그 감지 및 HTML 적용
        let lastIndex = 0;
        let html = '';
        
        for (const match of hashtagMatches) {
            // 해시태그 앞의 일반 텍스트 추가
            html += text.substring(lastIndex, match.index);
            
            // 해시태그 추가 (스타일 적용)
            html += `<span class="hashtag">${match.matchText}</span>`;
            
            lastIndex = match.index + match.length;
        }
        
        // 남은 텍스트 추가
        html += text.substring(lastIndex);
        
        return html;
    };
    
    return { formatMessageWithHashtags };
};

/**
 * 사용자 메시지 버블 컴포넌트
 */
export const UserMessageBubble: React.FC<MessageContent> = ({ 
    content, 
    timestamp 
}) => {
    const { formatMessageWithHashtags } = useMessageHashtagFormatting();

    return (
        <Flex 
            justify="flex-end"
            align="flex-end"
            style={{ width: '100%', marginBottom: '8px' }}
        >
            
            <Box style={{ maxWidth: '445px' }}>
                <Paper
                    p="md"
                    radius="md"
                    bg="#E7F5FF"
                    shadow="none"
                    style={{
                        width: '100%',
                        fontSize: '14px',
                        lineHeight: '1.5'
                    }}
                >
                    <div
                        style={{
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                        }}
                        className="message-content"
                        dangerouslySetInnerHTML={{ 
                            __html: formatMessageWithHashtags(content) 
                        }}
                    />
                </Paper>
            </Box>
        </Flex>
    );
};

/**
 * AI 메시지 버블 컴포넌트
 */
export const AiMessageBubble: React.FC<MessageContent> = ({ 
    content, 
    timestamp
}) => {
    // 해시태그 스타일링 함수 사용 제거
    // const { formatMessageWithHashtags } = useMessageHashtagFormatting();

    return (
        <Flex
            align="flex-end"
            style={{
                width: '100%',
                marginBottom: '12px',
                justifyContent: 'flex-start'
            }}
        >
            <Box style={{ maxWidth: '445px' }}>
                <Paper
                    p="sm"
                    radius="md"
                    bg="#F8F9FA"
                    shadow="none"
                    style={{
                        width: '100%',
                        fontSize: '14px',
                        lineHeight: '1.5',
                    }}
                >
                    <div 
                        style={{ 
                            flex: 1, 
                            minWidth: 0,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            padding: '0 2px'
                        }} 
                        className="message-bubble"
                    >
                        <MarkdownViewer markdown={content} />
                    </div>
                </Paper>
            </Box>
            

        </Flex>
    );
};

/**
 * MessageBubble 팩토리 컴포넌트
 * 메시지 타입에 따라 적절한 버블 컴포넌트를 반환
 */
export interface MessageBubbleProps extends MessageContent {
    isUser: boolean;
}

const MessageBubble: React.FC<MessageBubbleProps> = (props) => {
    const { isUser, ...restProps } = props;
    
    return isUser ? 
        <UserMessageBubble {...restProps} /> : 
        <AiMessageBubble {...restProps} />;
};

export default MessageBubble; 